import React from "react";
import { Button, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import {
    deleteObstacleIndex,
    deletePanelIndex,
    deleteSegmentIndex,
} from "../ThreeJs/ReactStates/states";
import { themeState } from "../lib/atom";
import { POLYGONS } from "../ThreeJs/Global/variables";

/**
 * Context-aware delete confirmation modal.
 * Figures out whether a section, obstacle, or panel(s) is being deleted from
 * the recoil delete-index atoms and renders matching copy.
 *
 * Callers whose target isn't tracked by those atoms (e.g. ruler measurements)
 * can pass `title`/`description` to supply their own copy.
 */
const DeleteConfirmation = ({ onConfirm, onCancel, title: titleOverride, description: descriptionOverride }) => {
    const theme = useRecoilValue(themeState);
    const segIndex = useRecoilValue(deleteSegmentIndex);
    const obsIndex = useRecoilValue(deleteObstacleIndex);
    const panIndex = useRecoilValue(deletePanelIndex);

    const { title, description } = (() => {
        if (titleOverride || descriptionOverride) {
            return {
                title: titleOverride || "Delete Item?",
                description: descriptionOverride || "This action cannot be undone.",
            };
        }

        if (segIndex !== null && segIndex !== undefined) {
            const panelCount = POLYGONS?.[segIndex]?.panels?.length || 0;
            return {
                title: "Delete Section?",
                description:
                    panelCount > 0
                        ? `This will remove the section and all ${panelCount} ${
                              panelCount === 1 ? "panel" : "panels"
                          }. This action cannot be undone.`
                        : "This will remove the section. This action cannot be undone.",
            };
        }

        if (obsIndex !== null && obsIndex !== undefined) {
            return {
                title: "Delete Obstacle?",
                description:
                    "This will remove the selected obstacle. This action cannot be undone.",
            };
        }

        if (panIndex !== null && panIndex !== undefined) {
            const count = Array.isArray(panIndex) ? panIndex.length : 1;
            return {
                title: count > 1 ? `Delete ${count} Panels?` : "Delete Panel?",
                description:
                    count > 1
                        ? `This will remove ${count} selected panels. This action cannot be undone.`
                        : "This will remove the selected panel. This action cannot be undone.",
            };
        }

        return {
            title: "Delete Item?",
            description: "This action cannot be undone.",
        };
    })();

    return (
        <Modal
            open
            centered
            closable={false}
            maskClosable
            footer={null}
            width={372}
            onCancel={onCancel}
            className={`delete-modal ${theme ? "dark" : ""}`}
            wrapClassName="delete-modal-wrap"
            styles={{ body: { padding: 0 } }}
        >
            <div className="delete-modal__content">
                <div className="delete-modal__icon" aria-hidden="true">
                    <DeleteOutlined />
                </div>

                <h2 className="delete-modal__title">{title}</h2>
                <p className="delete-modal__desc">{description}</p>

                <div className="delete-modal__actions">
                    <Button
                        className="delete-modal__btn delete-modal__btn--cancel"
                        onClick={onCancel}
                        block
                    >
                        Cancel
                    </Button>
                    <Button
                        className="delete-modal__btn delete-modal__btn--delete"
                        onClick={onConfirm}
                        block
                        danger
                        type="primary"
                    >
                        Delete
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DeleteConfirmation;
